import React from "react";
import { Palette } from "lucide-react";
import {
  DEFAULT_HOST_THEME,
  HOST_THEME_IDS,
  HOST_THEMES,
} from "../../lib/hostThemes";
import { Button } from "../ui";
import Card from "../ui/Card";
import { FieldHint, FORM_STYLE_CARD_CLASS, SectionTitle } from "../createEvent/CreateEventFormUi";
import HostOnboardingStickyActions, {
  ONBOARDING_DESKTOP_ACTIONS,
} from "./HostOnboardingStickyActions";

/**
 * Optional public theme pick, then finish.
 */
export default function HostOnboardingThemeStep({
  themeId,
  onThemeChange,
  saving,
  onFinish,
  finishLabel = "Finish setup →",
}) {
  const label = saving ? "Finishing…" : finishLabel;

  return (
    <div className="space-y-5" data-testid="host-onboarding-theme">
      <Card className={FORM_STYLE_CARD_CLASS}>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <Palette className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
            Pick a theme
          </span>
        </SectionTitle>
        <FieldHint className="text-[13px]">
          Shows on your public host page, school, and classes. Change anytime.
        </FieldHint>
        <div
          className="mt-5 grid gap-3 sm:grid-cols-2"
          role="radiogroup"
          aria-label="Host page themes"
          data-testid="host-onboarding-theme-picker"
        >
          {HOST_THEME_IDS.map((id) => {
            const meta = HOST_THEMES[id];
            const selected = themeId === id;
            const { canvas, ink, muted, cta } = meta.preview;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`host-onboarding-theme-${id}`}
                onClick={() => onThemeChange(id)}
                className={`text-left rounded-lg2 border p-3.5 min-h-[44px] motion-safe:transition-[box-shadow,border-color] motion-safe:duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 ${
                  selected ? "ring-2 ring-offset-2 ring-offset-canvas" : ""
                }`}
                style={{
                  background: canvas,
                  color: ink,
                  borderColor: selected ? cta : meta.dark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.1)",
                  ...(selected ? { boxShadow: `0 0 0 1px ${cta}`, "--tw-ring-color": cta } : null),
                }}
              >
                <span
                  className="flex h-10 rounded-md overflow-hidden border"
                  style={{
                    borderColor: meta.dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
                  }}
                  aria-hidden
                >
                  <span className="flex-1" style={{ background: canvas }} />
                  <span className="w-8" style={{ background: cta }} />
                  <span className="w-6" style={{ background: ink }} />
                </span>
                <span className="mt-2.5 block font-ui font-semibold text-[14px]" style={{ color: ink }}>
                  {meta.name}
                  {id === DEFAULT_HOST_THEME ? (
                    <span className="ml-1.5 font-medium" style={{ color: muted }}>
                      · default
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 block font-ui text-[12px] leading-snug" style={{ color: muted }}>
                  {meta.description}
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className={ONBOARDING_DESKTOP_ACTIONS}>
        <span className="flex-1" aria-hidden />
        <Button
          type="button"
          variant="primary"
          size="lg"
          disabled={saving}
          onClick={onFinish}
          data-testid="host-onboarding-theme-finish"
          className="min-w-[10rem]"
        >
          {label}
        </Button>
      </div>

      <HostOnboardingStickyActions testId="host-onboarding-theme-sticky">
        <Button
          type="button"
          variant="primary"
          size="lg"
          disabled={saving}
          onClick={onFinish}
          className="flex-1"
          data-testid="host-onboarding-theme-finish-sticky"
        >
          <span className="truncate">{label}</span>
        </Button>
      </HostOnboardingStickyActions>
    </div>
  );
}
